import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Card, Col, Row } from "react-bootstrap";
import { Title } from "@fyrstain/hl7-front-library";
import { NarrativeDisplay } from "@fyrstain/fhir-front-library";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { Patient, Person, Practitioner } from "fhir/r5";
import JunoPage from "../../components/JunoPage/JunoPage";
import { useErrorNavigation } from "../../services/useErrorNavigation";
import PatientService from "../../services/PatientService";
import PersonService from "../../services/PersonService";
import PractitionerService from "../../services/PractitionerService";
import {
  findLinkedPersonsForTarget,
  findResourceByIdOrIdentifier,
  formatAge,
  formatBirthDate,
  formatStatusValue,
  getAddressEntries,
  getFamilyName,
  getFullName,
  getGivenNames,
  getIdentifiers,
  getPrimaryIdentifier,
  getQualificationEntries,
  getResourceSection,
  getTelecomEntries,
  IndividualResource,
  IndividualResourceType,
  normalizeIndividualResourceType,
  resolveLinkedIndividuals,
} from "../../utils/individuals";
import "./IndividualDetails.css";

type IndividualDetailsProps = {
  forcedResourceType?: IndividualResourceType;
};

type RelationTarget = {
  id: string;
  label: string;
  resourceType: IndividualResourceType;
};

// Helpers
function deduplicateRelations(items: RelationTarget[]): RelationTarget[] {
  const seen = new Set<string>();

  return items.filter((item) => {
    const key = `${item.resourceType}-${item.id}`;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

const IndividualDetails: FunctionComponent<IndividualDetailsProps> = ({
  forcedResourceType,
}) => {
  //////////////////////////////
  //        Navigation        //
  //////////////////////////////

  useTranslation();
  const navigate = useNavigate();
  const { resourceType: resourceTypeParam, resourceId, userId } = useParams();
  const targetResourceType =
    forcedResourceType ??
    normalizeIndividualResourceType(resourceTypeParam) ??
    "Person";
  const targetResourceId = resourceId ?? userId ?? "";

  const [loading, setLoading] = useState(false);
  const [fitFooter, setFitFooter] = useState(false);
  const [resource, setResource] = useState<IndividualResource | null>(null);
  const [persons, setPersons] = useState<Person[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);

  const onError = useErrorNavigation();

  //////////////////////////////
  //      Data loading        //
  //////////////////////////////

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [peopleResponse, patientsResponse, practitionersResponse] =
        await Promise.all([
          PersonService.loadAllPersons(),
          PatientService.loadAllPatients(),
          PractitionerService.loadAllPractitioners(),
        ]);

      setPersons(peopleResponse);
      setPatients(patientsResponse);
      setPractitioners(practitionersResponse);

      let detailsResponse: IndividualResource | null = null;

      try {
        if (targetResourceType === "Patient") {
          detailsResponse = await PatientService.loadPatient(targetResourceId);
        } else if (targetResourceType === "Practitioner") {
          detailsResponse = await PractitionerService.loadPractitioner(
            targetResourceId
          );
        } else {
          detailsResponse = await PersonService.loadPerson(targetResourceId);
        }
      } catch (error) {
        if (targetResourceType === "Patient") {
          detailsResponse = findResourceByIdOrIdentifier(
            patientsResponse,
            targetResourceId
          );
        } else if (targetResourceType === "Practitioner") {
          detailsResponse = findResourceByIdOrIdentifier(
            practitionersResponse,
            targetResourceId
          );
        } else {
          detailsResponse = findResourceByIdOrIdentifier(
            peopleResponse,
            targetResourceId
          );
        }
      }

      if (!detailsResponse) {
        onError();
        return;
      }

      setFitFooter(!detailsResponse.text?.div);
      setResource(detailsResponse);
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  }, [onError, targetResourceId, targetResourceType]);

  useEffect(() => {
    load();
  }, [load]);

  //////////////////////////////
  //     Derived data         //
  //////////////////////////////

  const linkedIndividuals = useMemo(() => {
    if (targetResourceType !== "Person" || !resource || resource.resourceType !== "Person") {
      return [];
    }

    return resolveLinkedIndividuals(resource, patients, practitioners);
  }, [patients, practitioners, resource, targetResourceType]);

  const linkedPersons = useMemo(() => {
    if (!resource?.id || targetResourceType === "Person") {
      return [];
    }

    return findLinkedPersonsForTarget(persons, targetResourceType, resource.id);
  }, [persons, resource, targetResourceType]);

  const crossLinkedIndividuals = useMemo(() => {
    if (targetResourceType === "Person") {
      return [];
    }

    const targetType = targetResourceType === "Patient" ? "Practitioner" : "Patient";

    return deduplicateRelations(
      linkedPersons.flatMap((person) =>
        resolveLinkedIndividuals(person, patients, practitioners)
          .filter((linked) => linked.resourceType === targetType)
          .map((linked) => ({
            id: linked.id,
            label: linked.display,
            resourceType: linked.resourceType,
          }))
      )
    );
  }, [linkedPersons, patients, practitioners, targetResourceType]);

  const identifiers = useMemo(() => getIdentifiers(resource), [resource]);
  const telecomEntries = useMemo(() => getTelecomEntries(resource), [resource]);
  const qualificationEntries = useMemo(
    () =>
      resource?.resourceType === "Practitioner"
        ? getQualificationEntries(resource)
        : [],
    [resource]
  );
  const addressEntries = useMemo(
    () => (resource?.resourceType === "Patient" ? getAddressEntries(resource) : []),
    [resource]
  );

  const metaTags =
    resource?.meta?.tag?.map((tag, index) => ({
      key: `${tag.code ?? "tag"}-${index}`,
      value: tag.display ?? tag.code ?? "N/A",
    })) ?? [];

  const relationButtons = useMemo(() => {
    if (targetResourceType === "Person") {
      return linkedIndividuals.map((linked) => ({
        key: `${linked.resourceType}-${linked.id}`,
        label:
          linked.assurance && linked.assurance !== "N/A"
            ? `${linked.display} (${linked.resourceType}, ${linked.assurance})`
            : `${linked.display} (${linked.resourceType})`,
        onClick: () =>
          navigate(
            `/Individuals/${linked.resourceType}/${encodeURIComponent(linked.id)}`
          ),
      }));
    }

    return linkedPersons.map((person) => ({
      key: person.id ?? getPrimaryIdentifier(person),
      label: `${getFullName(person)} (${person.id ?? "N/A"})`,
      onClick: () =>
        navigate(`/Individuals/Person/${encodeURIComponent(person.id ?? "")}`),
    }));
  }, [linkedIndividuals, linkedPersons, navigate, targetResourceType]);

  const crossRelationButtons = useMemo(
    () =>
      crossLinkedIndividuals.map((linked) => ({
        key: `${linked.resourceType}-${linked.id}`,
        label: `${linked.label} (${linked.resourceType})`,
        onClick: () =>
          navigate(
            `/Individuals/${linked.resourceType}/${encodeURIComponent(linked.id)}`
          ),
      })),
    [crossLinkedIndividuals, navigate]
  );

  const statusValue = formatStatusValue(resource);
  const statusLabel =
    statusValue === "active"
      ? i18n.t("label.active")
      : statusValue === "inactive"
      ? i18n.t("label.inactive")
      : i18n.t("label.unknown");
  const genderLabel = resource?.gender ?? i18n.t("label.unknown");
  const pageTitleKey =
    targetResourceType === "Patient"
      ? "title.patientdetails"
      : targetResourceType === "Practitioner"
      ? "title.practitionerdetails"
      : "title.persondetails";

  const backSection = getResourceSection(targetResourceType);

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
    <JunoPage
      titleKey={pageTitleKey}
      loading={loading}
      fitFooter={fitFooter}
      needsLogin={true}
    >
      <div className="section">
        <Card className="card">
          <Card.Header className="individual-details-header">
            <div>
              <Title
                level={2}
                prefix={resource?.resourceType}
                content={resource?.id}
              />
              <div className="individual-details-subtitle">{getFullName(resource)}</div>
            </div>
            <Button
              onClick={() => navigate(`/Individuals/${backSection}`)}
              variant="outline-primary"
            >
              {i18n.t("button.backtolist")}
            </Button>
          </Card.Header>
          <Card.Body>
            {/* Narrative */}
            {resource?.text?.div && (
              <div className="individual-details-narrative">
                <NarrativeDisplay
                  resource={resource}
                  onError={onError}
                  message={i18n.t("title.error.narrativenotfound")}
                />
              </div>
            )}

            {/* Main details */}
            <div className="individual-details-layout">
              <div className="individual-details-badges">
                <Badge bg="dark">{resource?.resourceType ?? "N/A"}</Badge>
              </div>

              {/* Identity */}
              <Row className="g-3">
                <Col md={6} xl={3}>
                  <div className="individual-detail-card">
                    <span className="individual-detail-label">
                      {i18n.t("label.fullname")}
                    </span>
                    <span className="individual-detail-value">
                      {getFullName(resource)}
                    </span>
                  </div>
                </Col>
                <Col md={6} xl={3}>
                  <div className="individual-detail-card">
                    <span className="individual-detail-label">
                      {i18n.t("label.identifier")}
                    </span>
                    <span className="individual-detail-value">
                      {getPrimaryIdentifier(resource)}
                    </span>
                  </div>
                </Col>
                <Col md={6} xl={3}>
                  <div className="individual-detail-card">
                    <span className="individual-detail-label">
                      {i18n.t("label.resourceid")}
                    </span>
                    <span className="individual-detail-value">
                      {resource?.id ?? "N/A"}
                    </span>
                  </div>
                </Col>
                <Col md={6} xl={3}>
                  <div className="individual-detail-card">
                    <span className="individual-detail-label">
                      {i18n.t("label.status")}
                    </span>
                    <span className="individual-detail-value">
                      {statusLabel}
                    </span>
                  </div>
                </Col>
              </Row>

              <Row className="g-3">
                <Col md={6} xl={4}>
                  <div className="individual-detail-card">
                    <span className="individual-detail-label">
                      {i18n.t("label.gender")}
                    </span>
                    <span className="individual-detail-value">
                      {genderLabel}
                    </span>
                  </div>
                </Col>
                <Col md={6} xl={4}>
                  <div className="individual-detail-card">
                    <span className="individual-detail-label">
                      {i18n.t("label.birthdate")}
                    </span>
                    <span className="individual-detail-value">
                      {formatBirthDate(resource?.birthDate)}
                    </span>
                  </div>
                </Col>
                {resource?.birthDate && (
                  <Col md={6} xl={4}>
                    <div className="individual-detail-card">
                      <span className="individual-detail-label">
                        {i18n.t("label.age")}
                      </span>
                      <span className="individual-detail-value">
                        {formatAge(resource.birthDate)}
                      </span>
                    </div>
                  </Col>
                )}
              </Row>

              <Row className="g-3">
                <Col md={6}>
                  <div className="individual-detail-card">
                    <span className="individual-detail-label">
                      {i18n.t("label.lastname")}
                    </span>
                    <span className="individual-detail-value">
                      {getFamilyName(resource)}
                    </span>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="individual-detail-card">
                    <span className="individual-detail-label">
                      {i18n.t("label.firstname")}
                    </span>
                    <span className="individual-detail-value">
                      {getGivenNames(resource)}
                    </span>
                  </div>
                </Col>
              </Row>

              {/* Contact details */}
              <div className="individual-detail-card">
                <span className="individual-detail-label">
                  {i18n.t("label.identifiers")}
                </span>
                <div className="individual-detail-list">
                  {identifiers.length > 0 ? (
                    identifiers.map((identifier) => (
                      <div className="individual-detail-item" key={identifier.key}>
                        <strong>{identifier.value}</strong>
                        <span>
                          {i18n.t("label.system")}: {identifier.system}
                        </span>
                        <span>
                          {i18n.t("label.use")}: {identifier.use}
                        </span>
                      </div>
                    ))
                  ) : (
                    <span className="individual-detail-empty">N/A</span>
                  )}
                </div>
              </div>

              <div className="individual-detail-card">
                <span className="individual-detail-label">
                  {i18n.t("label.telecoms")}
                </span>
                <div className="individual-detail-list">
                  {telecomEntries.length > 0 ? (
                    telecomEntries.map((telecom) => (
                      <div className="individual-detail-item" key={telecom.key}>
                        <strong>{telecom.value}</strong>
                        <span>
                          {i18n.t("label.type")}: {telecom.system}
                        </span>
                        <span>
                          {i18n.t("label.use")}: {telecom.use}
                        </span>
                      </div>
                    ))
                  ) : (
                    <span className="individual-detail-empty">N/A</span>
                  )}
                </div>
              </div>

              {addressEntries.length > 0 && (
                <div className="individual-detail-card">
                  <span className="individual-detail-label">
                    {i18n.t("label.addresses")}
                  </span>
                  <div className="individual-detail-list">
                    {addressEntries.map((address) => (
                      <div className="individual-detail-item" key={address.key}>
                        <strong>{address.value}</strong>
                        <span>
                          {i18n.t("label.use")}: {address.use}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {qualificationEntries.length > 0 && (
                <div className="individual-detail-card">
                  <span className="individual-detail-label">
                    {i18n.t("label.qualifications")}
                  </span>
                  <div className="individual-detail-list">
                    {qualificationEntries.map((qualification) => (
                      <div
                        className="individual-detail-item"
                        key={qualification.key}
                      >
                        <strong>{qualification.label}</strong>
                        <span>
                          {i18n.t("label.issuer")}: {qualification.issuer}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Relations */}
              <div className="individual-detail-card">
                <span className="individual-detail-label">
                  {targetResourceType === "Person"
                    ? i18n.t("label.linkedresources")
                    : i18n.t("label.linkedpersons")}
                </span>
                <div className="individual-detail-chip-list">
                  {relationButtons.length > 0 ? (
                    relationButtons.map((button) => (
                      <Button
                        className="rounded-pill"
                        key={button.key}
                        onClick={button.onClick}
                        size="sm"
                        variant="outline-primary"
                      >
                        {button.label}
                      </Button>
                    ))
                  ) : (
                    <span className="individual-detail-empty">N/A</span>
                  )}
                </div>
              </div>

              {/* Cross references */}
              {crossRelationButtons.length > 0 && (
                <div className="individual-detail-card">
                  <span className="individual-detail-label">
                    {targetResourceType === "Patient"
                      ? i18n.t("label.linkedpractitioners")
                      : i18n.t("label.linkedpatients")}
                  </span>
                  <div className="individual-detail-chip-list">
                    {crossRelationButtons.map((button) => (
                      <Button
                        className="rounded-pill"
                        key={button.key}
                        onClick={button.onClick}
                        size="sm"
                        variant="outline-secondary"
                      >
                        {button.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="individual-detail-card">
                <span className="individual-detail-label">{i18n.t("label.tags")}</span>
                <div className="individual-detail-chip-list">
                  {metaTags.length > 0 ? (
                    metaTags.map((tag) => (
                      <Badge bg="secondary" key={tag.key}>
                        {tag.value}
                      </Badge>
                    ))
                  ) : (
                    <span className="individual-detail-empty">N/A</span>
                  )}
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    </JunoPage>
  );
};

export default IndividualDetails;
